import { Box, Button, Chip, Grid, Tooltip, Typography } from "@mui/material"
import { invoiceListStyles } from "../invoiceListStyles"
import { getFormattedDate, msToTime } from "../../../../../utilities/helpers/DateTimeFormatters"
import { geColorBasedOnPriorityInMinutes, getColorBasedOnServiceLevel, renderAutoDispatchedColumn, renderFlaggedCell } from "../InvoiceStaticComponents/InvoiceStaticComponents"
import { InvoicesResponse } from "../../../../../utilities/services/DeliveryRouteService"
import { ReactComponent as WarningIcon } from '../../../../../assets/warning.svg'
import { ReactComponent as CheckMarkIcon } from '../../../../../assets/checkmark.svg'
import Colors from "../../../../../assets/Colors"
import { Language, SupportedLanguage } from "../../../../../language/LanguageTypes"   

type InvoiceColumnsProps ={
    language: Language
    currentLanguage: SupportedLanguage
    isCompanyOwned: boolean
    setInvoiceDetails: (value: any) => void
    setOpenEditInvoice: (value: boolean) => void
    setLocationInvoiceDetails: (value: any) => void
    setIsBuildRoute: (value: boolean) => void
    setOpenLocationUpdate: (value: boolean) => void
}

export const getInvoiceListColumns = ({
    language,
    currentLanguage,
    isCompanyOwned,
    setInvoiceDetails,
    setOpenEditInvoice,
    setLocationInvoiceDetails,
    setIsBuildRoute,
    setOpenLocationUpdate
}: InvoiceColumnsProps) => [
        {
            field: 'invoice',
            headerName: language[currentLanguage].invoice.toUpperCase(),
            minWidth: 200,
            editable: false
        },
        {
            field: 'customer',
            headerName: language[currentLanguage].customerText,
            minWidth: 350,
            editable: false,
            renderCell: (params: any) => {
                return (
                    <Tooltip title={params.row.customer}>
                        <Typography sx={invoiceListStyles.storeName}>{params.row.customer}</Typography>
                    </Tooltip>
                )
            }
        },
        {
            field: 'orderNumber',
            headerName: language[currentLanguage]?.orderNumber?.toUpperCase(),
            minWidth: 200,
            editable: false,
        },
        {
            field: 'orderChannel',
            headerName: language[currentLanguage]?.orderChannel?.toUpperCase(),
            minWidth: 200,
            editable: false
        },
        {
            field: 'orderedAt',
            headerName: language[currentLanguage].orderedAt.toUpperCase(),
            type: 'date',
            valueFormatter: (params: any) => {
                return getFormattedDate(params.value)
            },
            minWidth: 250,
            filterable: false,
            editable: false
        },
        {
            field: 'timeElapsed',
            headerName: language[currentLanguage].timeElapsed.toUpperCase(),
            valueFormatter: (params: any) => {
                return msToTime(params.value)
            },
            filterable: false,
            minWidth: 150,
            editable: false
        },
        {
            field: 'priority',
            headerName: language[currentLanguage].priorityInMins.toUpperCase(),
            renderCell: (params: any) => {
                const chipColor = getColorBasedOnServiceLevel(params.row.serviceLevel?.toUpperCase(), isCompanyOwned)
                return (
                    <Chip
                        style={{
                            height: '20px',
                            width: '160px',
                            borderRadius: '3px',
                            color: params.row.serviceLevel !== "AUTODASH" ? Colors.napaGrey2 : Colors.white,
                            backgroundColor: isCompanyOwned
                                ? chipColor
                                : !!params.row.serviceLevel //for Independent stores- if serviceLevel is null, we use priority in min to determine color
                                    ? chipColor
                                    : params.row.priority
                                        ? geColorBasedOnPriorityInMinutes(params.row.priority)
                                        : '#E49600'
                        }}
                        label={isCompanyOwned
                            ? (!!params.row.serviceLevel && params.row.serviceLevel?.toUpperCase() !== 'BRONZE') ? params.row.serviceLevel?.toUpperCase() : 'SILVER'
                            : params.row.priority && params.row.serviceLevel
                                ? `${params.row.serviceLevel} - ${params.row.priority} mins`
                                : params.row.serviceLevel
                                    ? `${params.row.serviceLevel}`
                                    : params.row.priority
                                        ? `${params.row.priority} mins`
                                        : 'BRONZE'} />
                )
            },
            minWidth: 250,
            editable: false
        },
        {
            field: 'invoiceFlagged',
            headerName: language[currentLanguage].invoiceFlagged.toUpperCase(),
            filterable: false,
            minWidth: 150,
            editable: false,
            renderCell: (params: any) => renderFlaggedCell(params)
        },
        {
            field: 'autoDispatchTime',
            headerName: language[currentLanguage].autoDispatching,
            renderCell: (params: any) => {
                return params.row.autoDispatch || params.row.serviceLevel === 'AUTODASH' ? renderAutoDispatchedColumn(params) : ''
            },
            filterable: false,
            minWidth: 250,
            editable: false
        },
        {
            field: 'viewDetails',
            headerName: '',
            minWidth: 50,
            editable: false,
            filterable: false,
            renderCell: (params: any) => {
                return (
                    <Button
                        sx={invoiceListStyles.viewDetailsButton}
                        onClick={async () => {
                            setInvoiceDetails(params.row.invoiceDetails)
                            setOpenEditInvoice(true)
                        }}>
                        {language[currentLanguage].viewDetails}
                    </Button>
                )
            }
        }, {
            field: 'address',
            headerName: 'Location',
            filterable: false,
            minWidth: 250,
            editable: false,
            renderCell: (params: any) => {
                const location = params.row.location
                if (params.row.multipleAddress) {
                    return <Box display={"flex"} flexDirection={"row"} gap={"8px"} alignItems={"center"}
                        onClick={() => {
                            if (params.row.multipleAddress) {
                                let invoceDetails: InvoicesResponse[] = []
                                invoceDetails.push(params.row.invoiceDetails)
                                setLocationInvoiceDetails(invoceDetails)
                                setIsBuildRoute(false)
                                setOpenLocationUpdate(true)
                            }

                        }}>
                        {params.row.reqAddressUpdate
                            ? <WarningIcon />
                            : <CheckMarkIcon />}
                        <Typography sx={invoiceListStyles.cellStyleParagaraph2} color={'#0066CB'}>Multiple Addresses</Typography>
                    </Box>
                }
                return (
                    <Box display={"flex"} flexDirection={"row"} gap={"8px"} alignItems={"center"}>
                        <Grid display={"flex"} flexDirection={"column"}>
                            <Typography sx={invoiceListStyles.cellStyleParagaraph2}>{location.addressLine1}</Typography>
                            <Typography sx={invoiceListStyles.cellStyleParagaraph2}>{location.city}, {location.state} {location.zipCode}</Typography>
                        </Grid>
                    </Box>
                )
            }
        },
        {
            field: 'deliveryNote',
            headerName: 'DELIVERY NOTE',
            filterable: false,
            minWidth: 250,
            editable: false,
            renderCell: (params: any) => {
                return (
                    <Tooltip title={params.row.notes}>
                        <Typography sx={invoiceListStyles.storeName}>{params.row.notes}</Typography>
                    </Tooltip>
                )
            }
        },
        {
            field: 'parts',
            headerName: 'PART #',
            minWidth: 0,
            editable: false
        },
        {
            field: 'partsDescription',
            headerName: 'PARTS DESC',
            filterable: false,
            minWidth: 0,
            editable: false
        }
    ]